import { Alert, Button, Card, Input, Select, Typography } from "antd";
import type { Page } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { PAYMENT_STATE, PAYMENT_TYPE } from "@/meta/purchase/payment";
import { SORT } from "@/meta/common";
import { dateFormat } from "@/utils";
import { formatNumber } from "@/utils/number";
import { getIdentityByPayType } from "@/views/pages/purchasingManage/index";

export interface PaymentRow {
    id: number | string;
    projectName?: string;
    type?: number | string;
    payCustomerEnterprise?: { name?: string; realName?: string };
    collectCustomerEnterprise?: { name?: string; realName?: string };
    amount?: number;
    updatedTime?: string;
    state?: number | string;
}

export interface PaymentQuery {
    page: number;
    size: number;
    keyword?: string;
    enterprise?: string;
    type?: number;
    state?: number[];
    sortKey?: string;
    sortOrder?: string;
}

export interface PaymentPageProps {
    loadPayments?: (query: PaymentQuery) => Promise<Page<PaymentRow>>;
}

// 对齐旧 Vue useTable 的默认分页契约，付款列表首屏每页请求 10 条。
const defaultQuery: PaymentQuery = { page: 1, size: 10 };

/** 复用 Vue 付款管理的分页接口，保持旧版服务路径和查询参数契约。 */
export async function loadPayments(query: PaymentQuery): Promise<Page<PaymentRow>> {
    const { data } = await request.service.get<Page<PaymentRow>>("/admin/purchase/payment/paging", {
        params: query
    });
    return data;
}

/** 显示付款单位和收款单位的企业名称，兼容旧接口返回的企业对象字段。 */
const enterpriseName = (enterprise?: PaymentRow["payCustomerEnterprise"]) =>
    enterprise?.name || enterprise?.realName || "--";

/** 显示付款类型或付款状态的枚举文案。 */
const enumLabel = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number | string) =>
    value == null ? "--" : meta.getIntroByCode(Number(value)) || "--";

/** 沿用旧 Vue 付款列表的状态视觉规则，已完成状态使用弱化文本。 */
const paymentStateColor = (value?: number | string) =>
    Number(value) === PAYMENT_STATE.DONE.code ? "#00000066" : "#000000e0";

/** 将筛选表单值转换为 Vue ProTable 发送的分页参数。 */
const buildQuery = (keyword: string, enterprise: string, type?: number, state: number[] = []): PaymentQuery => ({
    ...defaultQuery,
    ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
    ...(enterprise.trim() ? { enterprise: enterprise.trim() } : {}),
    ...(type == null ? {} : { type }),
    ...(state.length ? { state } : {})
});

/** 采购管理-付款列表：覆盖 Vue 首屏查询、筛选、分页、详情入口和失败反馈。 */
export function PaymentPage({ loadPayments: load = loadPayments }: PaymentPageProps) {
    const [keyword, setKeyword] = useState("");
    const [enterprise, setEnterprise] = useState("");
    const [type, setType] = useState<number>();
    const [state, setState] = useState<number[]>([]);
    const [query, setQuery] = useState<PaymentQuery>(defaultQuery);
    const [rows, setRows] = useState<PaymentRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        void load(query)
            .then(result => {
                if (!active) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!active) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("付款管理加载失败");
            });
        return () => {
            active = false;
        };
    }, [load, query]);

    // 将 zhihao-ui-react 的排序事件映射为旧 Vue paymentService 的接口参数。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        const nextQuery = { ...query, page: 1 };
        delete nextQuery.sortKey;
        delete nextQuery.sortOrder;
        if (prop === "updatedTime" && order) {
            nextQuery.sortKey = "updated_time";
            nextQuery.sortOrder = SORT.getIntroByCode(order);
        }
        setQuery(nextQuery);
    };

    // 用 zhihao-ui-react 列契约承接 Vue 付款列表的字段、枚举、排序和详情跳转。
    const columns: TableColumnProps<PaymentRow>[] = [
        {
            key: "id",
            label: "编号",
            prop: "id",
            render: ({ row }) => <Link to={`/purchasingManage/payment/info/${row.id}`}>{String(row.id)}</Link>
        },
        { key: "projectName", label: "项目名称", prop: "projectName", render: ({ row }) => row.projectName || "--" },
        { key: "type", label: "类型", prop: "type", render: ({ row }) => enumLabel(PAYMENT_TYPE, row.type) },
        {
            key: "payCustomerEnterprise",
            label: "付款单位",
            prop: "payCustomerEnterprise",
            render: ({ row }) => (
                <div className="flex flex-col">
                    <span>{enterpriseName(row.payCustomerEnterprise)}</span>
                    <span className="text-sm text-black/65">{getIdentityByPayType(Number(row.type), 0)}</span>
                </div>
            )
        },
        {
            key: "collectCustomerEnterprise",
            label: "收款单位",
            prop: "collectCustomerEnterprise",
            render: ({ row }) => (
                <div className="flex flex-col">
                    <span>{enterpriseName(row.collectCustomerEnterprise)}</span>
                    <span className="text-sm text-black/65">{getIdentityByPayType(Number(row.type), 1)}</span>
                </div>
            )
        },
        {
            key: "amount",
            label: "金额 (元)",
            prop: "amount",
            render: ({ row }) => formatNumber(row.amount, 2)
        },
        {
            key: "updatedTime",
            label: "更新时间",
            prop: "updatedTime",
            sortable: true,
            render: ({ row }) => (row.updatedTime ? dateFormat(row.updatedTime, DATETIME_PICKER_FORMAT) : "--")
        },
        {
            key: "state",
            label: "状态",
            prop: "state",
            render: ({ row }) => (
                <span style={{ color: paymentStateColor(row.state) }}>{enumLabel(PAYMENT_STATE, row.state)}</span>
            )
        }
    ];

    return (
        <section aria-label="payment-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<PaymentRow>
                    data={rows}
                    columns={columns}
                    emptyText="暂无付款记录"
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                付款管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <div className="mb-4 flex flex-wrap items-center gap-2">
                                <Input
                                    aria-label="付款编号或项目名称"
                                    placeholder="输入付款编号、项目名称搜索"
                                    value={keyword}
                                    onChange={event => setKeyword(event.target.value)}
                                />
                                <Input
                                    aria-label="付款单位或收款单位"
                                    placeholder="输入付款单位、收款单位搜索"
                                    value={enterprise}
                                    onChange={event => setEnterprise(event.target.value)}
                                />
                                <Select
                                    aria-label="付款类型"
                                    allowClear
                                    options={PAYMENT_TYPE.options}
                                    placeholder="请选择类型"
                                    value={type}
                                    onChange={value => setType(value)}
                                />
                                <Select
                                    aria-label="付款状态"
                                    allowClear
                                    mode="multiple"
                                    options={PAYMENT_STATE.options}
                                    placeholder="请选择付款状态"
                                    value={state}
                                    onChange={value => setState(value)}
                                />
                                <Button
                                    type="primary"
                                    onClick={() => setQuery(buildQuery(keyword, enterprise, type, state))}
                                >
                                    查询
                                </Button>
                                <Button
                                    onClick={() => {
                                        setKeyword("");
                                        setEnterprise("");
                                        setType(undefined);
                                        setState([]);
                                        setQuery(defaultQuery);
                                    }}
                                >
                                    重置
                                </Button>
                            </div>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧版 Element Plus 分页的默认 sizes 选项，切换条数后回到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
                {total === 0 ? (
                    <Button
                        aria-label="下一页"
                        className="self-end"
                        onClick={() => setQuery(current => ({ ...current, page: current.page + 1 }))}
                    >
                        下一页
                    </Button>
                ) : null}
            </Card>
        </section>
    );
}

export default PaymentPage;
